import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { LoginRequest, type LoginRequest as LoginRequestType } from "@vedaunion/shared";
import { EditorialAuthShell } from "../components/auth/EditorialAuthShell";
import { AuthInput } from "../components/auth/AuthInput";
import { useLogin, useMe } from "../lib/auth";

/**
 * Where to go after signing in — and only somewhere on this site.
 *
 * `?next=` comes off the URL, which means it comes from whoever wrote the
 * link. `//evil.example` is a protocol-relative URL: the history API follows
 * it off-site, so a "sign in to continue" link could land somebody on a copy
 * of this page that keeps what they type. One `/` and no second one, or the
 * dashboard.
 */
function safeNext(raw: string | null): string {
  if (raw === null || raw === '') return '/dashboard';
  const dodgy = !raw.startsWith("/") || raw.startsWith("//") || raw.charAt(1) === "\\";
  if (dodgy) return "/dashboard";
  return raw;
}

export default function LoginPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const me = useMe();
  const { mutateAsync, isPending, error } = useLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginRequestType>({
    resolver: zodResolver(LoginRequest),
    defaultValues: { email: "", password: "" },
  });

  useEffect(() => {
    if (me.data?.user) {
      navigate(safeNext(search.get("next")), { replace: true });
    }
  }, [me.data?.user, navigate, search]);

  const resetSuccess = search.get("reset") === "success";

  const onSubmit = async (values: LoginRequestType) => {
    try {
      await mutateAsync(values);
      navigate(safeNext(search.get("next")), { replace: true });
    } catch (err) {
      setError("password", {
        type: "server",
        message:
          (err as { body?: { message?: string }; message?: string })?.body?.message ??
          (err as Error)?.message ??
          "Sign-in failed. Please try again.",
      });
    }
  };

  return (
    <EditorialAuthShell
      eyebrow="Sign in"
      headline={
        <>
          Welcome{" "}
          <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
            back
          </span>
          .
        </>
      }
      banner={
        resetSuccess ? (
          <p>
            <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
              Password updated.
            </span>{" "}
            Sign in with your new password.
          </p>
        ) : null
      }
      verse={{
        deva: "सत्यमेव जयते",
        iast: "Satyam eva jayate",
        english: "Truth alone triumphs.",
        source: "Muṇḍaka Upaniṣad 3.1.6",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <AuthInput
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          {...register("email")}
          error={errors.email?.message}
        />
        <AuthInput
          label="Password"
          type="password"
          autoComplete="current-password"
          {...register("password")}
          error={errors.password?.message}
        />

        <div className="auth-actions">
          <button type="submit" className="btn-primary auth-submit" disabled={isPending}>
            <span>{isPending ? "Signing in…" : "Sign in"}</span>
            <span className="btn-arrow-icon" aria-hidden>
              →
            </span>
          </button>
        </div>

        {error && !errors.password && (
          <p className="auth-error mt-3">— {error.message}</p>
        )}

        <div className="auth-links">
          <Link to="/forgot-password">Forgot password?</Link>
          <Link to="/register">Create an account →</Link>
        </div>
      </form>
    </EditorialAuthShell>
  );
}
